import { useMemo } from 'react'
import { useTableData } from '@/hooks/use-table-data'
import { useTablePersistence } from './use-table-persistence'

interface UseTableWithPersistenceConfig {
  tableId: string
  endpoint: string
  queryKey: string
  initialFilters?: import('@tanstack/react-table').ColumnFiltersState
  searchFields?: string | string[]
  fieldMappings?: {
    filters?: (field: string, value: unknown) => { field: string; value: unknown } | null
    sorting?: (field: string) => string
  }
  debounceMs?: {
    filters?: number
    search?: number
    sorting?: number
  }
}

/**
 * Hook that combines table data management with persistence.
 *
 * This hook manages both the data fetching and the persistence of table
 * configuration (column visibility and page size) in a single place.
 *
 * @param config Configuration object for both data and persistence
 * @returns Combined table data and persistence state
 */
export function useTableWithPersistence<TData>(config: UseTableWithPersistenceConfig) {
  const { tableId, endpoint, queryKey, initialFilters, searchFields, fieldMappings, debounceMs } =
    config

  // Get persisted configuration
  const persistence = useTablePersistence({
    tableId,
    defaultPageSize: 20,
    defaultColumnVisibility: {},
  })

  // Use persisted page size as initial page size for data fetching
  const tableData = useTableData<TData>({
    endpoint,
    queryKey,
    initialPageSize: persistence.pageSize,
    initialFilters,
    searchFields,
    fieldMappings,
    debounceMs,
  })

  // Combine the results
  return useMemo(
    () => ({
      ...tableData,
      ...persistence,
    }),
    [tableData, persistence],
  )
}
